import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import gameApi from '../services/GameApiService';

export default function EmailSentPage() {
  const [email, setEmail] = useState('');
  const [adventureTitle, setAdventureTitle] = useState<string | null>(null);
  const [adventureId, setAdventureId] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  useEffect(() => {
    const storedEmail = sessionStorage.getItem('gameEmail');
    if (storedEmail) setEmail(storedEmail);

    const storedTitle = sessionStorage.getItem('gameAdventureTitle');
    if (storedTitle) setAdventureTitle(storedTitle);

    const storedAdventureId = sessionStorage.getItem('gameAdventureId');
    if (storedAdventureId) setAdventureId(storedAdventureId);
  }, []);

  const handleResend = async () => {
    if (!email || isResending) return;

    setIsResending(true);
    setResendMessage('');

    try {
      await gameApi.startGame(email, adventureId ?? undefined);
      setResendMessage('Email reenviado com sucesso!');
    } catch (error) {
      setResendMessage('Erro ao reenviar. Tenta novamente.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-primary to-accent rounded-full">
            <EnvelopeIcon className="w-10 h-10 text-white" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-3xl font-bold text-dark mb-2">
          Verifica o teu email
        </h1>

        {/* Adventure title (when known) */}
        {adventureTitle && (
          <p className="text-base text-gray-500 mb-6">
            Para começar: <span className="font-semibold text-dark">{adventureTitle}</span>
          </p>
        )}

        {/* Description */}
        <p className="text-lg text-gray-600 mb-2 mt-6">
          Enviámos um link mágico para
        </p>
        {email && (
          <p className="text-lg font-semibold text-dark mb-6">
            {email}
          </p>
        )}

        {/* Instructions */}
        <div className="bg-white rounded-2xl shadow-sm p-6 mb-8 text-left">
          <h3 className="font-semibold text-dark mb-3">Passos seguintes:</h3>
          <ol className="space-y-3 text-gray-600">
            <li className="flex items-start">
              <span className="bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">1</span>
              <span>Abre o teu email</span>
            </li>
            <li className="flex items-start">
              <span className="bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">2</span>
              <span>Procura um email da Selna (verifica o spam se não encontrares)</span>
            </li>
            <li className="flex items-start">
              <span className="bg-primary text-white w-6 h-6 rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0">3</span>
              <span>Clica no botão para começar a aventura!</span>
            </li>
          </ol>
        </div>

        {/* Resend Button */}
        {email && (
          <div className="mb-4">
            <button
              onClick={handleResend}
              disabled={isResending}
              className="inline-flex items-center text-primary hover:text-accent transition-colors disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-5 h-5 mr-2 ${isResending ? 'animate-spin' : ''}`} />
              {isResending ? 'A reenviar...' : 'Reenviar email'}
            </button>
            {resendMessage && (
              <p className={`mt-2 text-sm ${resendMessage.includes('sucesso') ? 'text-green-600' : 'text-red-500'}`}>
                {resendMessage}
              </p>
            )}
          </div>
        )}

        {/* Back Link */}
        <Link
          to="/"
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          ← Voltar ao catálogo
        </Link>
      </div>
    </div>
  );
}
